"use client";

import React from "react";
import { motion } from "framer-motion";

const cormorant = {
    className: "font-cormorant font-serif",
};

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export interface ManifestoCard {
    roman: string;
    title: string;
    description: string;
}

export interface GuestiqoPhilosophyBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    cards?: ManifestoCard[];
}

export default function GuestiqoPhilosophyBlock({
    tag = "// Creative Core",
    title = "Design Philosophy",
    description = "We bridge digital tools with physical hospitality, matching the precision of clean software with the warmth of a curated hotel.",
    cards = [
        {
            roman: "I",
            title: "Editorial Minimalism",
            description: "Drawn from printed boutique guidebooks. Generous margins, clean typeface scales, and restrained warm colors make guest screens look like a carefully printed booklet, not a digital utility dashboard."
        },
        {
            roman: "II",
            title: "Tactile Surfaces",
            description: "Warm stone backdrops, paper-like borders, and quiet shadows convey structure and trust, blending digital layouts with the physical texture of the properties they describe."
        },
        {
            roman: "III",
            title: "Progressive Disclosure",
            description: "Surfacing stay details iteratively. Guests only see details that align with their location: Wi-Fi on entry, local guidelines during the stay, and checkout steps on departure morning."
        },
        {
            roman: "IV",
            title: "Responsive-First Layouts",
            description: "Since over 90% of views happen on mobile screens, interfaces are optimized completely around touch ranges, large buttons, clean line-breaks, and quick viewport scales."
        }
    ]
}: GuestiqoPhilosophyBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#F2ECE4] text-[#1C1C1C] w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-20">
                    {tag && <span className="text-xs font-mono text-[#8C8276] uppercase tracking-widest block mb-4">{tag}</span>}
                    {title && <h2 className={`${cormorant.className} text-4xl md:text-6xl font-light text-[#1C1C1C] leading-tight`}>{title}</h2>}
                    {description && (
                        <p className="text-[#5E5E5E] text-sm mt-3 font-sans font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                    {cards.map((manifesto, i) => (
                        <motion.div 
                            key={i}
                            {...fadeUp}
                            transition={{ delay: i * 0.1 }}
                            className="bg-[#FAF7F2] border border-[#1C1C1C]/5 p-8 md:p-10 rounded-2xl relative overflow-hidden group shadow-xs hover:border-[#1C1C1C]/15 transition-all duration-500 flex flex-col justify-between"
                        >
                            {/* Large Watermark Letter behind */}
                            <div className={`${cormorant.className} absolute -bottom-10 -right-4 text-[140px] md:text-[180px] font-bold text-[#1C1C1C]/[0.02] group-hover:text-[#1C1C1C]/[0.04] select-none pointer-events-none transition-colors duration-500`}>
                                {manifesto.roman}
                            </div>
                            
                            <div className="relative z-10 space-y-6">
                                <h3 className={`${cormorant.className} text-2xl md:text-3xl text-[#1C1C1C] font-medium`}>
                                    {manifesto.title}
                                </h3>
                                <p className="text-sm text-[#5E5E5E] leading-relaxed font-sans font-light max-w-sm">
                                    {manifesto.description}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
